import { type CSSProperties } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Clock, Image } from 'lucide-react';
import type { LessonSuggestion } from '@formency/shared';

interface SuggestionCardProps {
  suggestion: LessonSuggestion;
  isSelected: boolean;
  isOtherSelected: boolean;
  onSelect: () => void;
}

export function SuggestionCard({
  suggestion,
  isSelected,
  isOtherSelected,
  onSelect,
}: SuggestionCardProps) {
  const cardStyle: CSSProperties = {
    width: '100%',
    borderRadius: 12,
    border: isSelected ? '1px solid var(--color-accent)' : '1px solid var(--color-surface-border)',
    background: isSelected
      ? 'color-mix(in srgb, var(--color-accent) 4%, var(--color-surface-solid))'
      : 'var(--color-surface-solid)',
    textAlign: 'left',
    transition: 'all 0.2s',
    cursor: 'pointer',
    opacity: isOtherSelected && !isSelected ? 0.5 : 1,
    padding: 0,
    fontFamily: 'var(--font-sans)',
    outline: 'none',
  };

  return (
    <button type="button" aria-label={suggestion.title} onClick={onSelect} style={cardStyle}>
      {/* Collapsed header — always visible */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 12,
          padding: '12px 16px',
        }}
      >
        <div style={{ minWidth: 0, flex: 1 }}>
          <p
            style={{
              fontSize: 14,
              fontWeight: 600,
              lineHeight: 1.3,
              color: 'var(--color-text)',
              margin: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {suggestion.title}
          </p>
          <p
            style={{
              marginTop: 2,
              fontSize: 12,
              color: 'var(--color-text-muted)',
              margin: '2px 0 0',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {suggestion.shortDescription}
          </p>
        </div>
      </div>

      {/* Expanded details */}
      <AnimatePresence>
        {isSelected && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            style={{ overflow: 'hidden' }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
                borderTop: '1px solid var(--color-surface-border)',
                padding: '12px 16px',
              }}
            >
              <p
                style={{
                  fontSize: 14,
                  lineHeight: 1.6,
                  color: 'var(--color-text-secondary)',
                  margin: 0,
                }}
              >
                {suggestion.description}
              </p>

              {/* Meta row */}
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 12,
                  fontSize: 12,
                  color: 'var(--color-text-muted)',
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Clock size={12} />~{suggestion.estimatedMinutes} min
                </span>
                {suggestion.sourcePages && (
                  <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Image size={12} />
                    {suggestion.sourcePages}
                    {suggestion.availableFigures != null &&
                      ` · ${suggestion.availableFigures} figures`}
                  </span>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </button>
  );
}
